import VampScoringSteps from "./VampScoringSteps";
import VampStandingPill from "./VampStandingPill";
import VampTrendChart from "./VampTrendChart";
import {
  accountReading,
  accountWarnings,
  caidReading,
  caidWarnings,
  fmtInt,
  summaryLine,
  type VampMonthContext,
  type VampSelection,
} from "./vampStanding";

interface VampDetailPanelProps {
  selection: VampSelection;
  ctx: VampMonthContext;
}

const BLENDED_TEXT =
  "Blended rate: a weighted average across every CAID underneath. Visa never calculates this, it scores each CAID on its own, but it is close to what your acquirer looks at, and they can act on it themselves.";

// Both warnings say the rate on screen is understated, so they belong together above the chart
// rather than in the headline, which states what the figure is.
function Warnings({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul className="mt-3 flex flex-col gap-1.5 rounded-lg bg-warning-50 px-3.5 py-2.5">
      {messages.map((message) => (
        <li key={message} className="text-[13px] leading-5 text-warning-700">
          {message}
        </li>
      ))}
    </ul>
  );
}

function LegendItem({ swatch, children }: { swatch: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px] text-[11px] leading-4 text-gray-500">
      {swatch}
      {children}
    </span>
  );
}

export default function VampDetailPanel({ selection, ctx }: VampDetailPanelProps) {
  const { account } = selection;
  const isCaid = selection.kind === "caid";
  const reading = isCaid ? caidReading(selection.caid, ctx) : accountReading(account, ctx);
  const { totals, unmatched } = reading;

  const title = isCaid ? `${account.name} · ${selection.caid.title}` : account.name;
  const sub = isCaid
    ? `BIN ${selection.caid.bin ?? "not on file"} · CAID ${selection.caid.caid ?? "not on file"}`
    : `${account.caids.length} CAIDs${unmatched ? " · none matched" : ""}`;
  const headlineInk = isCaid ? reading.rateInk : unmatched ? "text-gray-300" : "text-gray-900";
  const line = isCaid ? summaryLine(totals, reading.state, ctx.isCurrent) : BLENDED_TEXT;
  // Same derivation as the list, so the icon there and the text here can never disagree.
  const warnings = isCaid ? caidWarnings(selection.caid, ctx.month) : accountWarnings(account, ctx.month);

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-[22px] py-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="line-clamp-2 text-base font-semibold leading-6 text-gray-900">{title}</div>
          <div className="mt-px line-clamp-2 text-theme-xs text-gray-400" title={sub}>
            {sub}
          </div>
        </div>
        {isCaid && <VampStandingPill standing={reading.standing} />}
      </div>

      <div className={`mt-3.5 text-[32px] font-bold leading-10 tracking-[-0.02em] tabular-nums ${headlineInk}`}>{reading.rateLabel}</div>
      <div className="mt-1 text-[13px] leading-5 text-gray-600">{line}</div>

      <Warnings messages={warnings} />

      {isCaid && (
        <>
          <VampScoringSteps reading={reading} ctx={ctx} />
          <div className="mt-4">
            <div className="mb-[7px] flex flex-wrap items-center gap-[15px]">
              <LegendItem swatch={<span className="h-0.5 w-3.5 rounded-[1px] bg-gray-800" />}>VAMP rate %</LegendItem>
              <LegendItem swatch={<span className="h-[9px] w-[9px] rounded-sm bg-brand-400" />}>Fraud reports (TC40) {fmtInt(totals.tc40)}</LegendItem>
              <LegendItem swatch={<span className="h-[9px] w-[9px] rounded-sm bg-brand-700" />}>Disputes (TC15) {fmtInt(totals.tc15)}</LegendItem>
              <LegendItem swatch={<span className="w-3.5 border-t-2 border-dashed border-error-600" />}>1.5%</LegendItem>
            </div>
            <VampTrendChart days={selection.caid.days} ctx={ctx} showFloor={!unmatched} />
          </div>
        </>
      )}
    </div>
  );
}
